import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EV_STATUS } from "../lib/labels";
import { Chip, EmptyState, PageHeader } from "../components/common";
import { EvidenceReqCard } from "../components/panels";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TABS = [
  { id: "revision", label: "Esperando mi revisión" },
  { id: "mias", label: "Mis evidencias" },
  { id: "todas", label: "Todas" },
];

export default function Evidencias() {
  const [params, setParams] = useSearchParams();
  const vista = params.get("vista") || "revision";
  const estado = params.get("estado") || "";
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["evidence-reqs", vista, estado],
    queryFn: async () => (await api.get("/evidence-requirements", { params: { vista: vista === "todas" ? undefined : vista, status: estado || undefined } })).data,
  });

  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["dashboard-badges"] }); };

  return (
    <div data-testid="evidencias-page">
      <PageHeader title="Evidencias" subtitle="Flujo: requisito → aportación → revisión → aceptación o devolución. Las evidencias obligatorias bloquean los cierres." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={vista === t.id} onClick={() => setParams({ vista: t.id })} data-testid={`evidence-tab-${t.id}`}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${vista === t.id ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Select value={estado} onValueChange={(v) => setParams({ vista, estado: v === "todos" ? "" : v })}>
          <SelectTrigger className="ml-auto h-8 w-44" data-testid="evidence-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="todos">Todos los estados</SelectItem>{Object.entries(EV_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando evidencias…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin requisitos de evidencia" description="No hay requisitos en esta vista con los filtros actuales." testid="evidences-empty" />
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id}>
              {r.expediente && (
                <Link to={`/expedientes/${r.expediente_id}?tab=evidencias`} className="mb-1 block text-xs text-blue-700 hover:underline" data-testid={`evidence-exp-link-${r.id}`}>
                  {r.expediente.code} — {r.expediente.title}
                </Link>
              )}
              <EvidenceReqCard req={r} me={user} onChanged={refresh} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
